/* ------------------------------------------------------------------------------------------
  Accordion Locking Enhancer (drop-in replacement to add "locked open at breakpoint" support)
  - Safe to include AFTER your existing DavidAI / dui bundle.
  - Adds support for `data-dui-accordion-locked="sm md lg xl 2xl"` on:
      * the accordion container `[data-dui-accordion-container]` (affects all toggles inside)
      * or an individual toggle `[data-dui-accordion-toggle]`
  - Behavior:
      * if the current viewport matches any breakpoint declared in `data-dui-accordion-locked`
        the accordion panel is forced open and the toggle becomes non-closable (aria-disabled).
      * when the viewport shrinks below the locked breakpoint, accordions that were *forced open*
        will be closed automatically **unless** the user manually opened them while unlocked.
      * manual opens are preserved across breakpoint changes (i.e. if the user opens the panel,
        we mark it as manual so it won't be forcibly collapsed when leaving a locked breakpoint).
  - This file only augments the existing accordion system; it does NOT replace the entire DavidAI bundle.
  - Copy / paste this script after your existing JS (so it can wire onto elements already initialized).
------------------------------------------------------------------------------------------ */

(function () {
  'use strict';

  // breakpoint map (Tailwind defaults)
  const _DUY_BREAKPOINTS = { sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 };

  // small util: normalize attribute token list (space or comma separated)
  function _tokensFromAttr(attr) {
    if (!attr) return [];
    return String(attr).split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
  }

  // returns true if any of the tokens correspond to a matching min-width media query
  function _dui_matchesLockedSizes(lockedStr) {
    const tokens = _tokensFromAttr(lockedStr);
    if (!tokens.length) return false;
    return tokens.some(token => {
      const px = _DUY_BREAKPOINTS[token];
      if (!px) return false;
      try {
        return window.matchMedia(`(min-width: ${px}px)`).matches;
      } catch (e) {
        return false;
      }
    });
  }

  // helper: set panel open/closed using same visual approach (maxHeight) as original bundle
  // set aria-expanded on toggle and handle icon toggles if present.
  function _setPanelState(panel, toggle, open) {
    if (!(panel instanceof HTMLElement)) return;

    panel.style.maxHeight = open ? `${panel.scrollHeight}px` : '0';
    panel.classList.toggle('acc-open', !!open);

    if (toggle instanceof HTMLElement) {
      toggle.setAttribute('aria-expanded', String(open));
      toggle.classList.toggle('acc-open', !!open);
    }

    const item = toggle ? toggle.closest('[data-dui-accordion-container]') : null;
    if (item) {
      item.classList.toggle('acc-open', !!open);
    }

    const iconOpen = toggle ? toggle.querySelector('[data-dui-accordion-icon-open]') : null;
    const iconClose = toggle ? toggle.querySelector('[data-dui-accordion-icon-close]') : null;
    const iconAny = toggle ? toggle.querySelector('[data-dui-accordion-icon]') : null;

    if (iconOpen) iconOpen.style.display = open ? 'block' : 'none';
    if (iconClose) iconClose.style.display = open ? 'none' : 'block';
    if (iconAny) iconAny.classList.toggle('rotate-180', !!open);
  }

  // Ensure an element has the inline maxHeight set to its scrollHeight so it appears opened.
  function _forceOpenPanel(panel) {
    if (!(panel instanceof HTMLElement)) return;
    panel.style.maxHeight = `${panel.scrollHeight}px`;
    panel.setAttribute('data-dui-accordion-forced-open', 'true');
  }

  function _clearForcedOpen(panel) {
    if (!(panel instanceof HTMLElement)) return;
    panel.removeAttribute('data-dui-accordion-forced-open');
  }

  // Core: update a single toggle/panel to reflect locked state (called on init and on resize)
  // IMPORTANT: When leaving a locked state we will collapse panels that were forced open
  // unless the user manually opened them (tracked via data-dui-accordion-manual-open = "true").
  function _updateToggleLockState(toggle) {
    if (!(toggle instanceof HTMLElement)) return;
    const targetSel = toggle.getAttribute('data-dui-accordion-target');
    if (!targetSel || !targetSel.startsWith('#')) return;
    const panel = document.querySelector(targetSel);
    if (!panel) return;

    // compute locked status from toggle attribute or its nearest container
    const toggleLocked = toggle.getAttribute('data-dui-accordion-locked');
    const container = toggle.closest('[data-dui-accordion-container]');
    const containerLocked = container ? container.getAttribute('data-dui-accordion-locked') : null;

    const lockedNow = _dui_matchesLockedSizes(toggleLocked) || _dui_matchesLockedSizes(containerLocked);

    if (lockedNow) {
      // If locked now -> force open + mark toggle disabled
      // BUT if user previously manually closed it while unlocked, respect manual state:
      const userManualOpen = panel.getAttribute('data-dui-accordion-manual-open') === 'true';
      if (!userManualOpen) {
        _setPanelState(panel, toggle, true);
      } else {
        // user previously explicitly opened it -> keep it open, and still mark forced state
        _setPanelState(panel, toggle, true);
      }
      toggle.setAttribute('aria-disabled', 'true');
      panel.setAttribute('data-dui-accordion-locked', 'true');
      _forceOpenPanel(panel);
    } else {
      // Not locked: restore previous interactive behavior
      const wasForced = panel.getAttribute('data-dui-accordion-locked') === 'true' || panel.getAttribute('data-dui-accordion-forced-open') === 'true';
      panel.removeAttribute('data-dui-accordion-locked');
      _clearForcedOpen(panel);

      // Remove aria-disabled if present
      if (toggle.getAttribute('aria-disabled') === 'true') {
        toggle.removeAttribute('aria-disabled');
      }

      // If the panel was forced open previously and the user did NOT manually open it while unlocked,
      // then we should collapse it now. If the user manually opened it (data-dui-accordion-manual-open === "true"),
      // keep it open.
      const manualOpen = panel.getAttribute('data-dui-accordion-manual-open') === 'true';
      if (wasForced && !manualOpen) {
        // collapse it
        _setPanelState(panel, toggle, false);
      } else {
        // preserve existing aria-expanded state (if any), default to collapsed
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        _setPanelState(panel, toggle, expanded);
      }
    }
  }

  // click interceptor for toggles: ignores clicks when locked and otherwise toggles
  function _handleToggleClick(ev) {
    const toggle = ev.currentTarget;
    if (!(toggle instanceof HTMLElement)) return;
    const targetSel = toggle.getAttribute('data-dui-accordion-target');
    if (!targetSel || !targetSel.startsWith('#')) return;
    const panel = document.querySelector(targetSel);
    if (!panel) return;

    // recompute lock status (in case viewport changed)
    const toggleLocked = toggle.getAttribute('data-dui-accordion-locked');
    const container = toggle.closest('[data-dui-accordion-container]');
    const containerLocked = container ? container.getAttribute('data-dui-accordion-locked') : null;
    const lockedNow = _dui_matchesLockedSizes(toggleLocked) || _dui_matchesLockedSizes(containerLocked);

    if (lockedNow) {
      // ensure it's open and ignore click (non-closable)
      _setPanelState(panel, toggle, true);
      toggle.setAttribute('aria-disabled', 'true');
      // prevent default toggle behavior
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }

    // not locked -> allow toggling
    const currentlyOpen = toggle.getAttribute('aria-expanded') === 'true';
    const newState = !currentlyOpen;
    _setPanelState(panel, toggle, newState);

    // record manual open state so we don't collapse it incorrectly when leaving locked breakpoint
    // If user opens the panel, mark manual-open true; if user collapses it manually, mark false.
    panel.setAttribute('data-dui-accordion-manual-open', newState ? 'true' : 'false');
  }

  // Initialize wiring: attach click handlers to toggles and enforce locked state on load
  function _initAccordionLocks(root = document) {
    const toggles = Array.from(root.querySelectorAll('[data-dui-accordion-toggle]'));
    toggles.forEach(toggle => {
      if (toggle.__dui_locked_wired) return;
      toggle.addEventListener('click', _handleToggleClick);
      toggle.__dui_locked_wired = true;
    });

    // initial update for each toggle
    toggles.forEach(toggle => _updateToggleLockState(toggle));
  }

  // Recalculate on resize / orientation change. Use a debounced handler.
  let _resizeTimer = null;
  function _onResizeDebounced() {
    if (_resizeTimer) clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(() => {
      document.querySelectorAll('[data-dui-accordion-toggle]').forEach(t => _updateToggleLockState(t));
    }, 120);
  }

  // Observe DOM mutations to pick up dynamically added accordions/toggles
  const _observer = new MutationObserver(mutations => {
    let added = false;
    for (const m of mutations) {
      if (m.addedNodes && m.addedNodes.length) {
        added = true;
        break;
      }
    }
    if (added) {
      mutations.forEach(mu => {
        mu.addedNodes && mu.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          _initAccordionLocks(node);
        });
      });
      document.querySelectorAll('[data-dui-accordion-toggle]').forEach(t => _updateToggleLockState(t));
    }
  });

  // Public: programmatic API to set or clear locks for an accordion toggle or container
  function setAccordionLocked(selectorOrEl, lockValue) {
    let el;
    if (typeof selectorOrEl === 'string') el = document.querySelector(selectorOrEl);
    else el = selectorOrEl;
    if (!el) return;
    if (lockValue == null || lockValue === '') {
      el.removeAttribute('data-dui-accordion-locked');
    } else {
      el.setAttribute('data-dui-accordion-locked', String(lockValue));
    }
    if (el.matches && el.matches('[data-dui-accordion-toggle]')) {
      _updateToggleLockState(el);
    } else {
      el.querySelectorAll && el.querySelectorAll('[data-dui-accordion-toggle]').forEach(t => _updateToggleLockState(t));
    }
  }

  // Public: toggle by id (keeps parity with existing toggleAccordionById)
  function toggleAccordionById(id) {
    if (!id) return;
    const sel = id.startsWith('#') ? id : `#${id}`;
    const toggle = document.querySelector(`[data-dui-accordion-target="${sel}"]`);
    if (!toggle) return;
    if (toggle.getAttribute('aria-disabled') === 'true') return;
    const panel = document.querySelector(sel);
    if (!panel) return;
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    _setPanelState(panel, toggle, !isOpen);
    panel.setAttribute('data-dui-accordion-manual-open', (!isOpen).toString());
  }

  // Wire global listeners on DOM ready
  document.addEventListener('DOMContentLoaded', () => {
    _initAccordionLocks(document);
    _observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', _onResizeDebounced);
    window.addEventListener('orientationchange', _onResizeDebounced);

    // Expose helper API on window for convenience
    window.duiAccordionLock = {
      setAccordionLocked,
      toggleAccordionById,
      updateAll: () => document.querySelectorAll('[data-dui-accordion-toggle]').forEach(t => _updateToggleLockState(t))
    };
  });

  // If script is injected after DOMContentLoaded already fired
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(() => {
      _initAccordionLocks(document);
      _observer.observe(document.body, { childList: true, subtree: true });
    }, 50);
  }
})();